/**
 * Custom error classes for campsite reviews API operations
 * These provide consistent error messages for Sentry grouping
 * while preserving context data for debugging
 */

import { BaseApiFetchError } from '../../../utility/error/BaseApiFetchError'
import { BaseApiValidationError } from '../../../utility/error/BaseApiValidationError'

export class ReviewsFetchError extends BaseApiFetchError {
  public readonly campsiteSlug: string
  public readonly reviewParams: Record<string, any>

  constructor(
    url: string,
    campsiteSlug: string,
    reviewParams: Record<string, any>,
    originalError: any,
  ) {
    // Create params object from reviews-specific parameters
    const params = { campsiteSlug, ...reviewParams }
    super('campsite-reviews', url, params, originalError)

    // Override name for backwards compatibility using Object.defineProperty like CustomError
    Object.defineProperty(this, 'name', {
      value: 'ReviewsFetchError',
      enumerable: true,
    })
    this.campsiteSlug = campsiteSlug
    this.reviewParams = reviewParams
  }

  /**
   * Get context data for Sentry or logging
   * This includes all the variable data that shouldn't be in the error message
   * Extends base context with reviews-specific fields for backwards compatibility
   */
  override getContext() {
    const baseContext = super.getContext()
    return {
      ...baseContext,
      // Add reviews-specific context fields for debugging
      campsiteSlug: this.campsiteSlug,
      order: this.reviewParams.order,
      filters: JSON.stringify(this.reviewParams.filters || {}),
      page: this.reviewParams.page,
      limit: this.reviewParams.limit,
      reviewId: this.reviewParams.reviewId,
    }
  }
}

export class ReviewsValidationError extends BaseApiValidationError {
  public readonly campsiteSlug: string
  public readonly reviewParams: Record<string, any>

  constructor(
    url: string,
    campsiteSlug: string,
    reviewParams: Record<string, any>,
    rawResponse: any,
    originalError: any,
  ) {
    // Create params object from reviews-specific parameters
    const params = { campsiteSlug, ...reviewParams }
    super('campsite-reviews', url, params, rawResponse, originalError)

    // Override name for backwards compatibility using Object.defineProperty like CustomError
    Object.defineProperty(this, 'name', {
      value: 'ReviewsValidationError',
      enumerable: true,
    })
    this.campsiteSlug = campsiteSlug
    this.reviewParams = reviewParams
  }

  /**
   * Get context data for Sentry or logging
   * Extends base context with reviews-specific fields for backwards compatibility
   */
  getContext() {
    const baseContext = super.getContext()
    return {
      ...baseContext,
      // Add reviews-specific context fields for debugging
      campsiteSlug: this.campsiteSlug,
      order: this.reviewParams.order,
      filters: JSON.stringify(this.reviewParams.filters || {}),
      page: this.reviewParams.page,
      limit: this.reviewParams.limit,
      reviewId: this.reviewParams.reviewId,
    }
  }
}
